'use client';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, X, ScrollText, BookMarked, PlusCircle, Filter, Sparkles, ChevronDown } from 'lucide-react';
import WallGrid from './WallGrid';
import WallStats from './WallStats';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

const ERA_LABELS: Record<string, string> = {
  'era-1': 'Early Iron Age',
  'era-2': 'Kingdom of Mutapa',
  'era-3': 'Rozvi Empire',
  'era-4': 'Colonial Contact',
  'era-5': 'Anti Colonial Resistance',
  'era-6': 'Nationalist Period',
  'era-7': 'Post-Independence',
};

export type EraData = {
  id: string;
  label: string;
  dateRange: string;
  description: string;
  topics: TopicData[];
};

export type TopicData = {
  id: string;
  label: string;
  published: number;
  minCoverage: number;
  region?: string;
  audioCount?: number;
};

// BACKEND: GET /api/wall → returns eras with topics and coverage counts
export const wallData: EraData[] = [
  {
    id: 'era-1',
    label: 'Early Iron Age',
    dateRange: '200–900 CE',
    description: 'Early Bantu-speaking communities, stone building origins, and pre-agricultural settlements across the Zimbabwe plateau.',
    topics: [
      { id: 'topic-101', label: 'Iron Age Settlements', published: 3, minCoverage: 10 },
      { id: 'topic-102', label: 'Stone Building Origins', published: 2, minCoverage: 8 },
      { id: 'topic-103', label: 'Pre-agricultural Communities', published: 1, minCoverage: 8 },
      { id: 'topic-104', label: 'Khoisan Interaction', published: 0, minCoverage: 6 },
    ],
  },
  {
    id: 'era-2',
    label: 'Kingdom of Mutapa',
    dateRange: '900–1700 CE',
    description: 'The Mutapa state, Great Zimbabwe, gold trade with the Swahili coast, and early Portuguese contact.',
    topics: [
      { id: 'topic-201', label: 'Great Zimbabwe', published: 18, minCoverage: 12 },
      { id: 'topic-202', label: 'Gold Trade Routes', published: 11, minCoverage: 10 },
      { id: 'topic-203', label: 'Portuguese Contact', published: 8, minCoverage: 8 },
      { id: 'topic-204', label: 'Oral Law Traditions', published: 4, minCoverage: 10 },
      { id: 'topic-205', label: 'Mutapa Politics', published: 7, minCoverage: 10 },
    ],
  },
  {
    id: 'era-3',
    label: 'Rozvi Empire',
    dateRange: '1680–1866 CE',
    description: 'The Rozvi Mambo state, Ndebele migration, and the decline of the Zimbabwe plateau kingdoms.',
    topics: [
      { id: 'topic-301', label: 'Rozvi Mambo State', published: 4, minCoverage: 8 },
      { id: 'topic-302', label: 'Ndebele State Formation', published: 3, minCoverage: 8 },
      { id: 'topic-303', label: 'Great Zimbabwe (Rozvi)', published: 6, minCoverage: 8 },
      { id: 'topic-304', label: 'Nguni Migration', published: 1, minCoverage: 6 },
    ],
  },
  {
    id: 'era-4',
    label: 'Colonial Contact',
    dateRange: '1850–1900 CE',
    description: 'Missionary activity, the Pioneer Column, and the establishment of British South Africa Company rule.',
    topics: [
      { id: 'topic-401', label: 'Missionary Activity', published: 9, minCoverage: 8 },
      { id: 'topic-402', label: 'Pioneer Column', published: 7, minCoverage: 8 },
      { id: 'topic-403', label: 'Early Colonial Administration', published: 5, minCoverage: 8 },
      { id: 'topic-404', label: 'BSAC Concessions', published: 2, minCoverage: 6 },
    ],
  },
  {
    id: 'era-5',
    label: 'Anti Colonial Resistance',
    dateRange: '1890–1920 CE',
    description: 'The First and Second Chimurenga, spirit medium resistance, and colonial land dispossession.',
    topics: [
      { id: 'topic-501', label: 'Chimurenga Wars', published: 22, minCoverage: 12 },
      { id: 'topic-502', label: 'Matabeleland Uprisings', published: 4, minCoverage: 10 },
      { id: 'topic-503', label: 'Spirit Medium Resistance', published: 8, minCoverage: 10 },
      { id: 'topic-504', label: 'Colonial Land Acts', published: 11, minCoverage: 10 },
      { id: 'topic-505', label: 'Nehanda & Sekuru Kaguvi', published: 14, minCoverage: 10 },
    ],
  },
  {
    id: 'era-6',
    label: 'Nationalist Period',
    dateRange: '1950–1980 CE',
    description: 'The liberation struggle, ZAPU and ZANU formation, UDI, and the road to independence.',
    topics: [
      { id: 'topic-601', label: 'Liberation Struggle (1960s)', published: 19, minCoverage: 12 },
      { id: 'topic-602', label: 'ZAPU / ZANU Formation', published: 13, minCoverage: 10 },
      { id: 'topic-603', label: 'Urban Nationalism', published: 6, minCoverage: 10 },
      { id: 'topic-604', label: 'UDI Period', published: 9, minCoverage: 10 },
      { id: 'topic-605', label: 'Guerrilla Warfare Accounts', published: 7, minCoverage: 10 },
      { id: 'topic-606', label: 'Women in Liberation', published: 3, minCoverage: 8 },
    ],
  },
  {
    id: 'era-7',
    label: 'Post-Independence',
    dateRange: '1980–present',
    description: 'Independence, Gukurahundi, land reform, economic crisis, and contemporary history.',
    topics: [
      { id: 'topic-701', label: 'Independence 1980', published: 21, minCoverage: 12 },
      { id: 'topic-702', label: 'Gukurahundi', published: 5, minCoverage: 12 },
      { id: 'topic-703', label: 'Land Reform', published: 10, minCoverage: 10 },
      { id: 'topic-704', label: 'Economic Crisis', published: 8, minCoverage: 10 },
      { id: 'topic-705', label: 'Diaspora Communities', published: 2, minCoverage: 8 },
    ],
  },
];

export default function WallPage() {
  const [selectedEra, setSelectedEra] = useState<string | null>(null);
  const [filterCoverage, setFilterCoverage] = useState<'all' | 'gaps' | 'covered'>('all');
  const [keyword, setKeyword] = useState('');
  const [publishedCounts, setPublishedCounts] = useState<Record<string, number>>({});
  const [audioCounts, setAudioCounts] = useState<Record<string, number>>({});
  const [realtimeKey, setRealtimeKey] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [newlyFilledTopics, setNewlyFilledTopics] = useState<Set<string>>(new Set());

  // Fetch live published counts from Supabase
  const fetchPublishedCounts = useCallback(async () => {
    const supabase = createClient();
    const { data } = await supabase
      .from('contributions')
      .select('topic_id, audio_url')
      .eq('status', 'published');
    if (data) {
      const counts: Record<string, number> = {};
      const audio: Record<string, number> = {};
      for (const row of data) {
        if (row.topic_id) {
          counts[row.topic_id] = (counts[row.topic_id] || 0) + 1;
          if (row.audio_url) {
            audio[row.topic_id] = (audio[row.topic_id] || 0) + 1;
          }
        }
      }
      setPublishedCounts(counts);
      setAudioCounts(audio);
    }
  }, []);

  useEffect(() => {
    fetchPublishedCounts();
  }, [fetchPublishedCounts]);

  // Real-time subscription: refresh when a contribution status changes to published
  useEffect(() => {
    const supabase = createClient();
    const channel = supabase
      .channel('wall-contributions-realtime')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'contributions', filter: 'status=eq.published' },
        () => {
          fetchPublishedCounts();
          setRealtimeKey((k) => k + 1);
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'contributions', filter: 'status=eq.published' },
        () => {
          fetchPublishedCounts();
          setRealtimeKey((k) => k + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchPublishedCounts]);

  // Real-time subscription: refresh when a story is approved and linked to an archive
  useEffect(() => {
    const supabase = createClient();

    const handleArchiveContributionChange = async () => {
      // Snapshot counts before refresh to detect newly-filled topics
      const prevCounts = { ...publishedCounts };
      await fetchPublishedCounts();
      setRealtimeKey((k) => k + 1);

      // After a short delay (state has updated), detect topics that just crossed threshold
      setTimeout(() => {
        setPublishedCounts((latestCounts) => {
          const filled = new Set<string>();
          for (const era of wallData) {
            for (const topic of era.topics) {
              const prev = prevCounts[topic.id] ?? topic.published;
              const next = latestCounts[topic.id] ?? topic.published;
              if (prev < topic.minCoverage && next >= topic.minCoverage) {
                filled.add(topic.id);
              }
            }
          }
          if (filled.size > 0) {
            setNewlyFilledTopics(filled);
            // Clear the highlight after the animation completes
            setTimeout(() => setNewlyFilledTopics(new Set()), 3000);
          }
          return latestCounts;
        });
      }, 300);
    };

    const archiveChannel = supabase
      .channel('wall-archive-contributions-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'archive_contributions' },
        handleArchiveContributionChange
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'archive_contributions' },
        handleArchiveContributionChange
      )
      .subscribe();

    return () => {
      supabase.removeChannel(archiveChannel);
    };
  }, [fetchPublishedCounts, publishedCounts]);

  // Merge live counts into wallData
  const liveWallData = useMemo<EraData[]>(() => {
    if (Object.keys(publishedCounts).length === 0) return wallData;
    return wallData.map((era) => ({
      ...era,
      topics: era.topics.map((topic) => ({
        ...topic,
        published: publishedCounts[topic.id] ?? topic.published,
        audioCount: audioCounts[topic.id] ?? 0,
      })),
    }));
  }, [publishedCounts, audioCounts, realtimeKey]);

  const totalTopics = liveWallData.flatMap((e) => e.topics).length;
  const coveredTopics = liveWallData.flatMap((e) => e.topics).filter((t) => t.published >= t.minCoverage).length;
  const gapTopics = totalTopics - coveredTopics;

  const filteredEras = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return liveWallData
      .filter((e) => !selectedEra || e.id === selectedEra)
      .map((era) => {
        if (!kw) return era;
        const eraMatches = era.label.toLowerCase().includes(kw) || era.description.toLowerCase().includes(kw) || era.dateRange.toLowerCase().includes(kw);
        if (eraMatches) return era;
        const filteredTopics = era.topics.filter((t) => t.label.toLowerCase().includes(kw));
        if (filteredTopics.length === 0) return null;
        return { ...era, topics: filteredTopics };
      })
      .filter(Boolean) as EraData[];
  }, [selectedEra, keyword, liveWallData]);

  const hasActiveFilters = keyword.trim() !== '' || selectedEra !== null || filterCoverage !== 'all';

  const clearFilters = () => {
    setKeyword('');
    setSelectedEra(null);
    setFilterCoverage('all');
  };

  const noResults = filteredEras.length === 0 || filteredEras.every((era) => {
    const filtered = era.topics.filter((t) => {
      if (filterCoverage === 'gaps') return t.published < t.minCoverage;
      if (filterCoverage === 'covered') return t.published >= t.minCoverage;
      return true;
    });
    return filtered.length === 0;
  });

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
              The Wall
            </h1>
            <p className="text-sm max-w-2xl" style={{ color: 'var(--muted-foreground)' }}>
              Each block represents a curated historical topic. Filled stone means the topic has reached its minimum
              coverage threshold. Dashed gaps are where the historical record is thin — and where your contribution
              matters most.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              href="/archives"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-150"
              style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)', backgroundColor: 'var(--card)' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--primary)';
                (e.currentTarget as HTMLAnchorElement).style.color = 'var(--primary)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border)';
                (e.currentTarget as HTMLAnchorElement).style.color = 'var(--muted-foreground)';
              }}
            >
              <BookMarked size={14} />
              Browse Archives
            </Link>
            <Link
              href="/contribution-submission"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150"
              style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
            >
              <PlusCircle size={14} />
              Start a Project
            </Link>
          </div>
        </div>
      </div>

      <WallStats
        totalTopics={totalTopics}
        coveredTopics={coveredTopics}
        gapTopics={gapTopics}
      />

      {/* Inline Discover Tools */}
      <div className="mb-6 p-4 rounded-xl border" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <input
              type="text"
              placeholder="Search topics on the Wall…"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg border text-sm outline-none"
              style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)', color: 'var(--foreground)' }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            />
            {keyword && (
              <button onClick={() => setKeyword('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                <X size={12} style={{ color: 'var(--muted-foreground)' }} />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all"
            style={{ backgroundColor: showFilters ? 'var(--primary)' : 'var(--background)', borderColor: showFilters ? 'var(--primary)' : 'var(--border)', color: showFilters ? 'var(--primary-foreground)' : 'var(--foreground)' }}
          >
            <Filter size={14} />
            Filter by Era
            <ChevronDown size={13} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          <Link
            href="/contribution-submission"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
            style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
          >
            <Sparkles size={14} />
            Fill a Gap
          </Link>
        </div>
        {showFilters && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedEra(null)}
              className="px-3 py-1.5 rounded-lg border text-xs font-medium transition-all"
              style={{ backgroundColor: !selectedEra ? 'var(--primary)' : 'var(--background)', borderColor: !selectedEra ? 'var(--primary)' : 'var(--border)', color: !selectedEra ? 'var(--primary-foreground)' : 'var(--muted-foreground)' }}
            >
              All Eras
            </button>
            {Object.entries(ERA_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setSelectedEra(selectedEra === key ? null : key)}
                className="px-3 py-1.5 rounded-lg border text-xs font-medium transition-all"
                style={{ backgroundColor: selectedEra === key ? 'var(--primary)' : 'var(--background)', borderColor: selectedEra === key ? 'var(--primary)' : 'var(--border)', color: selectedEra === key ? 'var(--primary-foreground)' : 'var(--muted-foreground)' }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {/* Coverage filter row */}
        <div className="mt-3 flex flex-wrap gap-2">
          {(['all', 'gaps', 'covered'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setFilterCoverage(v)}
              className="px-3 py-1.5 rounded-lg border text-xs font-medium transition-all capitalize"
              style={{ backgroundColor: filterCoverage === v ? '#C17A3E22' : 'transparent', borderColor: filterCoverage === v ? 'var(--primary)' : 'var(--border)', color: filterCoverage === v ? 'var(--primary)' : 'var(--muted-foreground)' }}
            >
              {v === 'all' ? 'All Topics' : v === 'gaps' ? '⬜ Gaps only' : '🟫 Covered only'}
            </button>
          ))}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium"
              style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
            >
              <X size={11} /> Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Empty state */}
      {noResults ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <ScrollText size={48} className="mb-5 opacity-20" style={{ color: 'var(--muted-foreground)' }} />
          <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--foreground)' }}>
            {hasActiveFilters ? 'No topics match your filters' : 'The Wall is empty'}
          </h3>
          <p className="text-sm max-w-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
            {hasActiveFilters
              ? 'Try adjusting your search or filters to explore the archive.' :'No contributions have been published yet. Be the first to add to the historical record.'}
          </p>
          {hasActiveFilters ? (
            <button onClick={clearFilters} className="px-4 py-2 rounded-lg text-sm font-medium border" style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
              Clear filters
            </button>
          ) : (
            <Link href="/contribution-submission" className="px-5 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}>
              Submit a contribution
            </Link>
          )}
        </div>
      ) : (
        <WallGrid eras={filteredEras} coverageFilter={filterCoverage} newlyFilledTopics={newlyFilledTopics} />
      )}
    </div>
  );
}